import {
  BodyText,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNextButton,
  PaginationPreviousButton,
} from "@/components/ui";
import { useMemo, useState } from "react";
import { MessagesTable } from "./MessagesTable";
import {
  DEFAULT_ASYNC_MESSAGE_ARCHIVE_PER_PAGE,
  useAsyncMessageArchive,
} from "./network/useAsyncMessageArchive";

export const MessagesArchive = () => {
  const [page, setPage] = useState(1);
  const perPage = DEFAULT_ASYNC_MESSAGE_ARCHIVE_PER_PAGE;

  const { data, isError, isLoading } = useAsyncMessageArchive({
    page,
    perPage,
  });

  const pagination = data?.pagination ?? null;
  const messages = data?.asyncMessages;

  const normalizedPagination = useMemo(() => {
    if (!pagination) return null;

    const safeNumber = (value: unknown, fallback: number) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    };

    // Prefer backend `page` when present; otherwise use local state (what we requested).
    // If we default to 1 when the API omits `page`, page 2+ shows wrong "Page 1" / "Showing 1–N…".
    const normalizedPage = safeNumber(pagination.page ?? page, page);
    const normalizedPerPage = safeNumber(
      pagination.perPage ?? perPage,
      perPage,
    );
    const normalizedPages = safeNumber(pagination.pages ?? 1, 1);
    const normalizedTotal = safeNumber(pagination.total ?? 0, 0);

    // Axios response transform layer normalizes to camelCase.
    const hasNext =
      typeof pagination.hasNext === "boolean"
        ? pagination.hasNext
        : normalizedPage < normalizedPages;
    const hasPrev =
      typeof pagination.hasPrev === "boolean"
        ? pagination.hasPrev
        : normalizedPage > 1;

    return {
      hasNext,
      hasPrev,
      nextNum: pagination.nextNum ?? null,
      page: normalizedPage,
      pages: normalizedPages,
      perPage: normalizedPerPage,
      total: normalizedTotal,
    };
  }, [page, pagination, perPage]);

  const pageSummary = useMemo(() => {
    if (!normalizedPagination) return null;

    const startIndex =
      (normalizedPagination.page - 1) * normalizedPagination.perPage;
    const endIndex = startIndex + (messages?.length ?? 0);

    return {
      start: normalizedPagination.total === 0 ? 0 : startIndex + 1,
      end: Math.min(endIndex, normalizedPagination.total),
      total: normalizedPagination.total,
      page: normalizedPagination.page,
      pages: normalizedPagination.pages,
    };
  }, [messages?.length, normalizedPagination]);

  return (
    <div className="flex flex-col gap-4">
      <MessagesTable
        messages={messages}
        archived={true}
        isLoading={isLoading}
        isError={isError}
      />

      {normalizedPagination && normalizedPagination.pages > 1 && (
        <div className="flex flex-col gap-2">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPreviousButton
                  disabled={!normalizedPagination.hasPrev || isLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </PaginationPreviousButton>
              </PaginationItem>
              <PaginationItem>
                <PaginationNextButton
                  disabled={!normalizedPagination.hasNext || isLoading}
                  onClick={() =>
                    setPage((p) => Math.min(normalizedPagination.pages, p + 1))
                  }
                >
                  Next
                </PaginationNextButton>
              </PaginationItem>
            </PaginationContent>
          </Pagination>

          {pageSummary && (
            <div className="flex justify-between items-center">
              <BodyText size="sm" textColor="secondary">
                Showing {pageSummary.start}-{pageSummary.end} of{" "}
                {pageSummary.total} messages
              </BodyText>
              <BodyText size="sm" textColor="secondary">
                Page {pageSummary.page} of {pageSummary.pages}
              </BodyText>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
